import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Toggle } from "../components/ui/toggle"
import { cn } from "../lib/utils"

export function ToggleRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain.
  const atomDefaults = useAtomDefaults("toggle")
  const resolvedVariantRaw = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const [pressed, setPressed] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    false,
    Boolean,
  )

  const label = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const variant = resolvedVariantRaw === "outline" ? "outline" : "default"
  const isDisabled = element.disabled === true

  const handlePressedChange = (newPressed: boolean) => {
    setPressed(newPressed)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: newPressed })
    dispatchEvent(element.id, "tap", { ...(itemContext ?? {}), pressed: newPressed })
  }

  return (
    // The data-testid + data-* markers live on the interactive control itself so
    // callers can read `.disabled` and a click drives onPressedChange (parity with
    // button/checkbox). A wrapping element would swallow both signals.
    <Toggle
      render={<button type="button" value={pressed ? "true" : "false"} />}
      variant={variant}
      pressed={pressed}
      onPressedChange={handlePressedChange}
      disabled={isDisabled}
      aria-label={element.ariaLabel ?? label}
      className={cn("inline-flex", element.className)}
      data-testid={`toggle-${element.id}`}
      data-atom-type="toggle"
      data-variant={resolvedVariantRaw}
      data-size={resolvedSize}
      data-value={pressed ? "true" : "false"}
    >
      {label}
    </Toggle>
  )
}
